"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useCookies } from "next-client-cookies";
import { getCookie } from "cookies-next";

import ModalComp from "@/Components/ModalComp/page";
import Navbar from "@/Components/Navbar/page";
import Loading from "../user/loading";

import EncryptData, { base64Encode, DecryptAES256, encryptCardAES256 } from "@/utils/Function/EnccryptDecrypt";
import fetchLogin from "@/utils/Api/fetchLogin/page";
import * as Constants from "@/utils/Constants/constants";
import { TbRefresh } from "react-icons/tb";
import CustomModal from "@/Components/CustomPasswordModal/PasswordModal";
import { logError, logInfo } from "@/utils/Logger/logger";
import { FiEye, FiEyeOff } from "react-icons/fi";

export default function Login() {
    const router = useRouter();
    const cookieStore = useCookies();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [captchaImage, setCaptchaImage] = useState("");
    const [captchaId, setCaptchaId] = useState("");
    const [userInput, setUserInput] = useState("");
    const [error, setError] = useState("");
    const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [toChangePass, setToChangePass] = useState(false);
    const [modal, setModal] = useState({ show: false, type: "", title: "", msg: "", btn: false });
    const [showPassword, setShowPassword] = useState(false);


    function handleTogglePassword() {
        setShowPassword(!showPassword);
    }

    const postJSON = async (url: string, body = {}) =>
        fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Access-Control-Allow-Origin": "*",
            },
            body: JSON.stringify(body),
        }).then((res) => res.json());

    const fetchCaptcha = async () => {
        try {
            const data = await postJSON(`${Constants.APIBASEURL}/access/generateCaptcha`, {
                captchaLength: 6,
            });

            if (data?.statusCode === 200 || data?.statusCode === "OK") {
                logInfo({
                    event: "generateCaptcha: Successfully retrieve data",
                    email,
                });
                setCaptchaImage(`data:image/png;base64,${data.obj?.captcha_image}`);
                setCaptchaId(data.obj?.captcha_id);
                setIsCaptchaVerified(false);
                setUserInput('');
            } else {
                logError({
                    event: "generateCaptcha: Failed to retrieve data",
                    email,
                    reason: data?.msg || data?.errMsg || "Invalid credentials",
                });
                console.error("Captcha fetch failed");
            }
        } catch (err) {
            console.error("Captcha fetch error:", err);
        }
    };

    const handleVerify = async () => {
        const payload = { captcha_id: captchaId, captcha: userInput };
        const token = base64Encode(`${email}${email}${email}`);
        const encCapt = encryptCardAES256(JSON.stringify(payload), token);

        try {
            const response = await fetch(`${Constants.APIBASEURL}/access/verifyCaptcha`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-client-token": token,
                    "x-encrypted-data": encCapt,
                },
                body: JSON.stringify(payload),
            });

            const encryptedResponseData = response.headers.get('x-encrypted-data');
            let headerResponseData = null;

            if (encryptedResponseData) {
                try {
                    const decryptedResponseData = DecryptAES256(encryptedResponseData, token);
                    headerResponseData = JSON.parse(decryptedResponseData);
                    console.log("Decrypted header response:", headerResponseData);
                } catch (err) {
                    console.error("Decryption error:", err);
                }
            }

            const data = await response.json();

            if (
                response.ok &&
                (data.statusCode === 200 || data.statusCode === "OK") &&
                JSON.stringify(headerResponseData) === JSON.stringify(data)
            ) {
                logInfo({
                    event: "verifyCaptcha: Successfully retrieve data",
                    email,
                });
                setIsCaptchaVerified(true);
                setError("");
            } else {
                logError({
                    event: "verifyCaptcha: Failed to retrieve data",
                    email,
                    reason: data?.msg || data?.errMsg || "Invalid credentials",
                });
                setIsCaptchaVerified(false);
                setUserInput('');
                setError("Captcha incorrect or data mismatch. Try again.");
            }
        } catch (err) {
            console.error("Request failed:", err);
            setError("Network error occurred. Please try again.");
            setIsCaptchaVerified(false);
        }
    };

    const handleRedirectUserType = async (email: string) => {
        try {
            const response = await fetch(`${Constants.APIBASEURL}/admin/access/getLandingPage`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();
            if (data?.statusCode === 200) {
                logInfo({
                    event: "getLandingPage: Successfully retrieve data",
                    email,
                });
                router.push(data.obj.link);
            } else {
                logError({
                    event: "getLandingPage: Failed to retrieve data",
                    email,
                    reason: data?.msg || data?.errMsg || "Invalid credentials",
                });
                router.push("/user/dashboard");
            }
        } catch (error) {
            console.error("Redirect failed:", error);
            router.push("/user/dashboard");
        }
    };

    const submitHandler = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        if (!email || !password) {
            setIsLoading(false);
            return setModal({
                show: true,
                type: Constants.MODAL_TYPE_ERROR,
                title: "Missing fields",
                msg: "Please fill in email and password",
                btn: true,
            });
        }

        try {
            const token = `${btoa(email)}.${EncryptData(password, btoa(email + email))}`;
            const resp = await fetchLogin(JSON.stringify({ token }), cookieStore);
            const data = await resp.json();

            if (data?.loggedIn) {
                logInfo({
                    event: "Login successful",
                    email,
                    userType: data?.userType || "",
                });
                if (data?.toChangePassword) {
                    setToChangePass(data?.toChangePassword);
                } else {
                    cookieStore.set(Constants.USER_CAPTURE, "ACTIVE", Constants.COOKIE_OPTIONS);
                    cookieStore.set("uLXizdwd-a", `1${Date.now()}`, Constants.COOKIE_OPTIONS);

                    if (data?.userName) {
                        await handleRedirectUserType(email);
                    } else {
                        router.push("/user/dashboard");
                    }
                }
            } else {
                await fetchCaptcha();
                logError({
                    event: "Login failed",
                    email,
                    reason: data?.msg || data?.errMsg || "Invalid credentials",
                });
                setIsCaptchaVerified(false);
                setUserInput("");
                setModal({
                    show: true,
                    type: Constants.MODAL_TYPE_ERROR,
                    title: "Login Failed",
                    msg: data?.msg || "Invalid credentials",
                    btn: true,
                });
            }
        } catch (err) {
            console.error("Login error:", err);
            await fetchCaptcha();
            setIsCaptchaVerified(false);
            setUserInput("");
            setModal({
                show: true,
                type: Constants.MODAL_TYPE_ERROR,
                title: "Login Error",
                msg: err?.toString() || "Unknown error",
                btn: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const closeModal = () => setModal({ show: false, type: "", title: "", msg: "", btn: false });

    useEffect(() => {
        fetchCaptcha();
    }, []);

    // useEffect(() => {
    //     if (isCaptchaVerified) {
    //         router.prefetch("/user/dashboard");
    //     }
    // }, [isCaptchaVerified]);

    return (
        <>
            <Navbar />
            <section className="pt-20 p-4 bg-blue-500 flex items-center justify-center min-h-screen">
                <div className="bg-gray-100 rounded-2xl shadow-xl overflow-hidden w-full max-w-sm">
                    <div className="p-8 text-center">
                        <h1 className="text-2xl font-bold text-gray-800">Admin Login</h1>
                        <p className="text-gray-600 mt-2">Welcome to ArthPay</p>

                        <form className="mt-6 text-left" onSubmit={submitHandler}>
                            <label className="block text-gray-700 mb-1">Email Address</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value?.toLowerCase())}
                                className="w-full px-4 py-3 rounded-lg bg-gray-200 border focus:border-blue-500 focus:bg-white text-black"
                                placeholder="Enter Email"
                                required
                            />

                            <div className="mt-4">
                                <label className="block text-gray-700">Password</label>
                                <div className="relative rounded-md shadow-sm">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        id="password"
                                        placeholder="Enter Password"
                                        className="w-full px-4 py-3 rounded-lg bg-gray-200 border focus:border-blue-500 focus:bg-white text-black"
                                        value={password}
                                        autoComplete="current-password"
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                        <button
                                            id="togglePass"
                                            type="button"
                                            onClick={handleTogglePassword}
                                            className="text-gray-600 hover:text-gray-800"
                                        >
                                            {showPassword ? <FiEye size={20} /> : <FiEyeOff size={20} />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="text-right my-2">
                                <Link href="/login/reset" className="text-sm text-gray-700 hover:text-blue-700">
                                    Forgot password?
                                </Link>
                            </div>

                            <label className="block text-gray-700 mb-1">Captcha</label>
                            <div className="flex items-center space-x-4">
                                {captchaImage && (
                                    <img
                                        src={captchaImage}
                                        alt="Captcha"
                                        className="h-10 w-auto border rounded"
                                    />
                                )}
                                <button type="button" onClick={fetchCaptcha} className="text-blue-500 hover:text-blue-700 text-xl">
                                    <TbRefresh />
                                </button>
                            </div>

                            <div className="flex items-center mt-2 space-x-2">
                                <input
                                    type="text"
                                    value={userInput}
                                    onChange={(e) => {
                                        setUserInput(e.target.value);
                                        setError("");
                                    }}
                                    className="flex-1 px-4 py-1.5 rounded-lg bg-gray-200 border focus:border-blue-500 focus:bg-white text-black"
                                    placeholder="Enter Captcha"
                                    disabled={isCaptchaVerified}
                                    required
                                />

                                <button
                                    type="button"
                                    onClick={handleVerify}
                                    disabled={isCaptchaVerified}
                                    className={`px-4 py-2 rounded-lg text-sm ${!isCaptchaVerified ? "bg-blue-600 hover:bg-blue-500 text-white" : "bg-green-500 text-white"
                                        }`}
                                >
                                    {isCaptchaVerified ? "Verified" : "Verify"}
                                </button>
                            </div>

                            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

                            <button
                                type="submit"
                                disabled={!isCaptchaVerified}
                                className={`w-full ${!isCaptchaVerified && "opacity-50"} bg-blue-500 hover:bg-blue-400 text-white font-semibold rounded-lg px-4 py-3 mt-4`}
                            >
                                Log In
                            </button>
                        </form>
                    </div>

                    <ModalComp
                        show={modal.show}
                        type={modal.type}
                        title={modal.title}
                        text={modal.msg}
                        close={closeModal}
                        showbtn={modal.btn}
                    />

                    {
                        toChangePass && (
                            <CustomModal
                                title="Password Expired"
                                message="Your password has expired or needs to be reset. Please update your password to continue."
                                onClose={() => {
                                    setToChangePass(false);
                                    router.push("/login/reset");
                                }}
                            />
                        )}

                    {isLoading && <Loading />}
                </div>
            </section>
        </>
    );
}